import { useEffect, useMemo, useRef, useState } from 'react';
import { EASTMONEY_ONLY, INSTRUMENTS } from '../../lib/instruments';
import { useIsMobile } from '../../lib/hooks';
import { useSelection } from '../../lib/selection';
import { Popover } from './Popover';

/**
 * 图表页的指数选择器 —— 挂在指数头部（原「上证指数」那个标题位）。
 *
 * ## 为什么图表页需要自己的入口
 *
 * 顶栏那排快切只列 7 个（`QUICK_SWITCH_CODES`），而且整条 `lg:flex` ——
 * **<1024px 完全不渲染**。顶栏搜索框的 `q` 只写不读（TopBar.tsx），
 * 是个死占位。于是「上证50 / 中证500 / 北证50 / 中证2000 / 恒生指数 /
 * 国企指数」这 6 个，图表页上一键都到不了，只能先去行情页。
 *
 * 这里列出全部 13 个（INSTRUMENTS 是全应用唯一数据源），并支持按名称或
 * 代码筛选 —— 13 项里输入「50」能同时命中 6 个，输入「hk」能捞出港股。
 */
export function InstrumentPicker() {
  const { instrument, selectOnly } = useSelection();
  return (
    <Popover
      variant="bare"
      label={instrument.name}
      width={248}
      title={`切换指数，当前 ${instrument.name}`}
    >
      {/* children 传函数即可拿到 close；下面的列表组件随面板挂载而挂载 */}
      {(close) => (
        <InstrumentList
          current={instrument.code}
          onPick={(code) => { selectOnly(code); close(); }}
        />
      )}
    </Popover>
  );
}

/**
 * 浮层内容。**单独拆一个组件**是刻意的：
 *
 *  · 它只在面板打开时挂载 → 挂载即聚焦、卸载即重置查询词，
 *    都不必手动写清理逻辑。
 *  · 复用 Popover 已有的外部点击 / Escape 归还焦点 / 视口溢出左移，
 *    不另写一套（Popover 注释明确要求该行为全项目仅一处实现）。
 */
function InstrumentList({ current, onPick }: {
  current: string;
  onPick: (code: string) => void;
}) {
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  /**
   * 桌面打开即聚焦，直接可打字。
   *
   * ⚠️ 手机上**不**聚焦：弹软键盘会盖掉半屏列表，而多数时候
   *    只是想扫一眼 13 个指数直接点。
   */
  useEffect(() => {
    if (!isMobile) inputRef.current?.focus();
  }, [isMobile]);

  const list = useMemo(() => {
    const k = q.trim().toLowerCase();
    if (!k) return INSTRUMENTS;
    // 三个字段都参与匹配：名称（"中证"）、代码（"000905"）、
    // klineSymbol（"hk" → 恒生科技 hkHSTECH 等）。只匹配前两个时，
    // 港股那三项无法通过任何关键词捞出来（名称是中文、代码里没有 hk）。
    return INSTRUMENTS.filter((i) =>
      i.name.toLowerCase().includes(k)
      || i.code.toLowerCase().includes(k)
      || i.klineSymbol.toLowerCase().includes(k));
  }, [q]);

  return (
    <div>
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="筛选指数（名称或代码）"
        aria-label="筛选指数"
        className="num w-full rounded border px-2 py-1 text-[12px] outline-none focus:border-[var(--accent)]"
        style={{ background: 'var(--bg-inset)', color: 'var(--text-1)', borderColor: 'var(--border)' }}
      />

      {/* 13 项约 364px，限高滚动 */}
      <div className="mt-1.5 max-h-[248px] overflow-y-auto">
        {list.map((i) => {
          const on = i.code === current;
          return (
            <button
              key={i.code}
              type="button"
              onClick={() => onPick(i.code)}
              aria-current={on}
              className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left transition-colors hover:bg-[var(--bg-hover)]"
              style={{ color: on ? 'var(--text-1)' : 'var(--text-2)' }}
            >
              <span className="flex-1 truncate text-[12px]">{i.name}</span>
              <span className="num shrink-0 text-[10px]" style={{ color: 'var(--text-3)' }}>
                {i.code}
              </span>
              {i.market === 'HK' && (
                <span className="shrink-0 rounded px-1 text-[9px]"
                  style={{ background: 'var(--bg-elevated)', color: 'var(--text-3)' }}>港股</span>
              )}
              {EASTMONEY_ONLY.has(i.code) && (
                <span className="shrink-0 rounded px-1 text-[9px]"
                  style={{ background: 'var(--bg-elevated)', color: 'var(--warn)' }}>仅东财</span>
              )}
            </button>
          );
        })}
        {list.length === 0 && (
          <div className="px-1.5 py-2 text-[12px]" style={{ color: 'var(--text-3)' }}>
            无匹配指数
          </div>
        )}
      </div>
    </div>
  );
}
